import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Copy, QrCode, CreditCard, Banknote, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MP_PUBLIC_KEY = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;

// Initialize MercadoPago SDK once
if (MP_PUBLIC_KEY) {
  initMercadoPago(MP_PUBLIC_KEY, { locale: 'pt-BR' });
}

const PaymentPage = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('pix'); // pix | credit_card | debit_card
  const [pixData, setPixData] = useState(null);
  const [generatingPix, setGeneratingPix] = useState(false);
  const [cpf, setCpf] = useState('');
  const [cardProcessing, setCardProcessing] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const pollRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRegistration();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${API}/registrations/me`, { headers: authHeaders });
      setRegistration(response.data);
      if (response.data.payment_status === 'pago') {
        setPaymentConfirmed(true);
      }
    } catch (error) {
      toast.error('Erro ao buscar inscrição');
      navigate('/inscricao');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePix = async () => {
    setGeneratingPix(true);
    try {
      const response = await axios.post(
        `${API}/payments/pix/create`,
        { cpf: cpf || null },
        { headers: authHeaders }
      );
      setPixData(response.data);
      toast.success('QR Code PIX gerado! Escaneie com seu app bancário');
      startPolling(response.data.payment_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar PIX');
    } finally {
      setGeneratingPix(false);
    }
  };

  const startPolling = (paymentId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${paymentId}`, { headers: authHeaders });
        if (res.data.payment_status === 'pago') {
          clearInterval(pollRef.current);
          setPaymentConfirmed(true);
          toast.success('Pagamento confirmado!');
          setTimeout(() => navigate('/cartao-inscricao'), 1800);
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
  };

  const handleCopyPix = () => {
    if (!pixData?.qr_code) return;
    navigator.clipboard.writeText(pixData.qr_code);
    toast.success('Código PIX copiado!');
  };

  const handleCardSubmit = async (formData) => {
    setCardProcessing(true);
    try {
      const payload = {
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments || 1,
        issuer_id: formData.issuer_id ? String(formData.issuer_id) : null,
        payer_email: formData.payer?.email,
        identification_type: formData.payer?.identification?.type || 'CPF',
        identification_number: formData.payer?.identification?.number || '',
      };
      const response = await axios.post(`${API}/payments/card/create`, payload, { headers: authHeaders });
      if (response.data.payment_status === 'pago') {
        setPaymentConfirmed(true);
        toast.success('Pagamento aprovado!');
        setTimeout(() => navigate('/cartao-inscricao'), 1500);
      } else if (response.data.payment_status === 'pendente') {
        toast.info('Pagamento em análise. Aguarde a confirmação.');
      } else {
        toast.error('Pagamento recusado. Tente outro cartão.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar cartão');
    } finally {
      setCardProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl font-unbounded">Carregando...</div>
      </div>
    );
  }

  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 border-2 border-primary rounded-full mb-6">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-unbounded font-black text-3xl mb-4 uppercase">
            <span className="text-white">Pagamento </span>
            <span className="text-primary">Confirmado</span>
          </h2>
          <p className="text-muted-foreground mb-6">Redirecionando para seu cartão de inscrição...</p>
          <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
        </motion.div>
      </div>
    );
  }

  const methods = [
    { id: 'pix', label: 'PIX', icon: QrCode, desc: 'Instantâneo' },
    { id: 'credit_card', label: 'Crédito', icon: CreditCard, desc: 'Até 12x' },
    { id: 'debit_card', label: 'Débito', icon: Banknote, desc: 'À vista' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/inscricao')}
            className="mb-6 text-muted-foreground hover:text-white font-mono uppercase text-xs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="font-unbounded font-bold text-4xl md:text-5xl tracking-tight uppercase">
            <span className="text-white">Finalize o </span>
            <span className="text-primary">Pagamento</span>
          </h1>
        </div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6 mb-6"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-muted-foreground text-xs uppercase font-mono tracking-wider">Inscrição</p>
              <p className="font-bold text-lg">{registration?.full_name}</p>
              <p className="text-sm text-muted-foreground">{registration?.team} • {registration?.level}</p>
            </div>
            <div className="text-right">
              <p className="text-muted-foreground text-xs uppercase font-mono">Total</p>
              <p className="text-3xl font-bold text-primary">R$ {registration?.amount.toFixed(2)}</p>
            </div>
          </div>
        </motion.div>

        {/* Method Tabs */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {methods.map((m) => {
            const Icon = m.icon;
            const active = method === m.id;
            return (
              <button
                key={m.id}
                data-testid={`payment-method-${m.id}`}
                onClick={() => { setMethod(m.id); setPixData(null); }}
                className={`p-4 border-2 transition-all ${active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-white/10 text-muted-foreground hover:border-white/30'}`}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" />
                <div className="font-unbounded font-bold uppercase text-sm">{m.label}</div>
                <div className="text-xs">{m.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Method Content */}
        <motion.div
          key={method}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-6"
        >
          {method === 'pix' && (
            <div>
              <h3 className="font-unbounded font-bold text-xl mb-4 uppercase">Pagar com PIX</h3>

              {!pixData ? (
                <div className="space-y-4">
                  <div>
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                      CPF (opcional)
                    </label>
                    <Input
                      data-testid="pix-cpf-input"
                      value={cpf}
                      onChange={(e) => setCpf(e.target.value)}
                      placeholder="000.000.000-00"
                      className="bg-secondary/30 border-white/10"
                    />
                  </div>
                  <Button
                    data-testid="generate-pix-button"
                    onClick={handleGeneratePix}
                    disabled={generatingPix}
                    className="w-full bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90 h-12"
                  >
                    {generatingPix ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Gerando...</>
                    ) : (
                      <><QrCode className="w-5 h-5 mr-2" />Gerar QR Code PIX</>
                    )}
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="text-center mb-6">
                    <div className="inline-block bg-white p-4 rounded">
                      {pixData.qr_code_base64 ? (
                        <img
                          src={`data:image/png;base64,${pixData.qr_code_base64}`}
                          alt="QR Code PIX"
                          className="w-64 h-64"
                          data-testid="pix-qr-image"
                        />
                      ) : (
                        <div className="w-64 h-64 flex items-center justify-center text-black">QR Code indisponível</div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 flex items-center justify-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Aguardando confirmação do pagamento...
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                      Copie o código PIX
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-secondary/50 border border-white/10 p-3 font-mono text-xs break-all max-h-24 overflow-auto">
                        {pixData.qr_code}
                      </div>
                      <Button
                        data-testid="copy-pix-button"
                        onClick={handleCopyPix}
                        variant="outline"
                        className="border-white/20 hover:border-primary hover:text-primary"
                      >
                        <Copy className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="bg-primary/10 border border-primary/30 p-4 rounded text-xs text-muted-foreground">
                    <strong className="text-primary">Como pagar:</strong> Abra o app do seu banco → PIX → Ler QR Code ou Copia e Cola → Confirme.
                    A confirmação é automática em alguns segundos.
                  </div>
                </div>
              )}
            </div>
          )}

          {(method === 'credit_card' || method === 'debit_card') && (
            <div>
              <h3 className="font-unbounded font-bold text-xl mb-4 uppercase">
                {method === 'credit_card' ? 'Cartão de Crédito' : 'Cartão de Débito'}
              </h3>
              {cardProcessing && (
                <div className="flex items-center justify-center gap-2 text-primary mb-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-mono uppercase text-xs">Processando pagamento...</span>
                </div>
              )}
              <div data-testid="card-payment-brick" className="bg-white rounded overflow-hidden">
                <CardPayment
                  initialization={{
                    amount: registration?.amount || 0,
                    payer: { email: '' },
                  }}
                  customization={{
                    paymentMethods: method === 'credit_card'
                      ? { maxInstallments: 12, types: { excluded: ['debit_card'] } }
                      : { types: { excluded: ['credit_card'] } },
                    visual: { style: { theme: 'default' } },
                  }}
                  onSubmit={handleCardSubmit}
                  onError={(err) => {
                    console.error('MP Brick error:', err);
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-4">
                Pagamento processado com segurança pelo MercadoPago
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentPage;
